import React, { HTMLInputTypeAttribute } from 'react';
import { TextField, InputAdornment, IconButton, TextFieldProps } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import styled from '@emotion/styled';
import { isValidCurrencyString } from 'utils/helpers';

export interface TextInputProps extends Omit<TextFieldProps, 'name' | 'defaultValues'> {
    icon?: React.ReactNode;
    iconPosition?: 'start' | 'end';
    width?: string | number;
    height?: string | number;
    multiline?: boolean;
    minRows?: number;
    backgroundColor?: string; // Add backgroundColor prop
    value?: any;
    name?: any;
    setValue?: any;
    type?: HTMLInputTypeAttribute | 'currency';
}

const formatInputAsCurrency = (value: string) => {
    const vText = value.replace('$', '').replace('.', '').replace(',', '');

    let vValue = 0.0;
    if (vText) {
        vValue = +vText / 100;
    }
    return '$' + vValue.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

const PrimaryTextInput = styled(TextField)(({ width, height, multiline, backgroundColor }: TextInputProps) => ({
    backgroundColor: backgroundColor || 'white', // Use the provided backgroundColor or default to 'white'
    width: width,
    height: height,
    '& .MuiInputBase-input': {
        height: height,
        padding: `0 2px 2px ${multiline ? 2 : 12}px`,
    },
}));

const TextInput: React.FC<TextInputProps> = ({
    icon = <SearchIcon />,
    iconPosition = 'start',
    width = 443,
    height = 50,
    placeholder = 'Search here',
    sx = {},
    label,
    onChange,
    setValue,
    name,
    multiline,
    minRows,
    backgroundColor = 'white', // Default backgroundColor to 'white'
    value,
    type,
    ...props
}) => {
    const handelChangetext = (event: any) => {
        if (onChange) {
            if (type === 'currency') {
                // CHECKING IF THE USER PRESSED ANY KEY EXCEPT NUMBER THEN WE ARE NOT FIRING THE ON CHANGE EVENT.
                // else we are converting the number to cents
                const newValue = event.target.value.replace(/[- #*;,.<>\{\}\[\]\\\/]/gi, '').replace('$', '');
                if (!isValidCurrencyString(newValue)) {
                    return;
                }
                event.target.value = newValue;
            }
            return onChange(event);
        }
        if (event.target.type === 'number') {
            setValue && setValue(event.target.name, parseFloat(event.target.value));
        } else {
            setValue && setValue(event.target.name, event.target.value);
        }
    };

    const adornment = icon && (
        <InputAdornment position={iconPosition}>
            <IconButton sx={{ color: '#202A44' }}>{icon}</IconButton>
        </InputAdornment>
    );

    return (
        <PrimaryTextInput
            placeholder={placeholder}
            width={width}
            height={height}
            sx={sx}
            label={label}
            name={name}
            // We are formatting the input to currency if type of input is currency and we will always
            // return the currency in cents.
            value={type === 'currency' ? formatInputAsCurrency(value) : value}
            onChange={(event) => handelChangetext(event)}
            multiline={multiline}
            minRows={minRows}
            backgroundColor={backgroundColor}
            InputProps={{
                [`${iconPosition}Adornment`]: adornment,
            }}
            {...props}
        />
    );
};

export default TextInput;
